import {IPatient, PatientState} from '../../models/patient';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {PhysicalActivityLevelEnum, RecordTypeEnum} from '../../enums/record';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {GenderEnum} from '../../models/user';
import {NzMarks} from 'ng-zorro-antd/slider';
import {PatientSphereAction, PatientSphereState, SPHERE} from '../../models/patient-sphere';
import {cloneDeep} from 'lodash';
import {Select, Store} from '@ngxs/store';
import {firstValueFrom, Observable, Subject, takeUntil} from 'rxjs';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgScrollbar} from 'ngx-scrollbar';
import {PatientGoalNumberTypeEnum} from '../../enums/patient-goal';
import {IUser} from '../../models/auth';
import {StorageService} from '../../services/storage/storage.service';
import {MiniSphereNamespace} from '../../namespaces/mini-sphere.namespace';
import {SphereModeEnum} from '../../enums/sphere/sphere-mode.enum';
import {MiniSphereState, RecordMiniSphereAction} from '../../models/record-mini-sphere/mini-sphere.redux';
import {IService, ServiceAction, ServiceState} from '../../models/service';
import {take} from 'rxjs/operators';

export interface ILevelActivityValues {
  NO_ACTIVITY: {
    days: string;
    description: string;
  };
  LIGHT_EXERCISE: {
    days: string;
    description: string;
  };
  MODERATE_EXERCISE: {
    days: string;
    description: string;
  };
  HEAVY_EXERCISE: {
    days: string;
    description: string;
  };
  VERY_HEAVY_EXERCISE: {
    days: string;
    description: string;
  };
}
@Component({
  selector: 'app-important-info-patient',
  templateUrl: './important-info-patient.component.html',
  styleUrls: ['./important-info-patient.component.scss'],
  animations: [fadeInAnimation]
})
export class ImportantInfoPatientComponent implements OnInit, OnDestroy {
  @Select(PatientSphereState.isSphereOpen) isSphereOpen$: Observable<boolean>;
  @Select(ServiceState.getServicePatientRecordMode) recordMode$: Observable<SphereModeEnum>;
  @ViewChild('scrollComponent') scrollComponent: NgScrollbar;

  private destroy$ = new Subject();
  viewInformation: boolean;
  patientRelevantData: SPHERE.ISphere;
  patientRelevantDataMiniSphere: MiniSphereNamespace.IMiniSphere;
  sphereModeEnum: typeof SphereModeEnum = SphereModeEnum;
  sphereType: SphereModeEnum = SphereModeEnum.COMPLETE;
  serviceId: string;
  physicalActivityLevelEnum = PhysicalActivityLevelEnum;
  loadData: boolean;

  levelActivity: ILevelActivityValues = {
    NO_ACTIVITY: {
      days: '',
      description: 'dormir o reclinarme'
    },
    LIGHT_EXERCISE: {
      days: '1-3 días u horas a la semana',
      description: 'Ej. estar sentada(o) o de pie: ej. pintar, manejar, planchar, cocinar, trabajo de oficina'
    },
    MODERATE_EXERCISE: {
      days: '3 a 5 días u horas a la semana',
      description: 'Ej. caminata moderada, elíptica, trotar, golf, trabajar en restaurante, cuidar a niños'
    },
    HEAVY_EXERCISE: {
      days: '6-7 días u horas a la semana',
      description: 'Ej. ciclismo, esquiar, tennis, ejercicios de resistencia, atletismo, caminata intensa, llevar una carga, etc. Con bastante desgaste'
    },
    VERY_HEAVY_EXERCISE: {
      days: '2 veces al día o 4 a 5 horas diarias',
      description: 'Ej. basquetbol, futbol americano, soccer, escalar montañas, entrenamiento militar, boxear, ciclismo, atletismo'
    }
  };
  sedentaryLevel = {
    LIGHT: 'tw-text-green-500',
    MEDIUM: 'tw-text-orange-500',
    HIGHT: 'tw-text-red-500'
  };
  marksEasyOrDifficult: NzMarks = {
    0: 'Imposible',
    2: 'Muy difícil',
    4: 'Difícil',
    6: 'Regular',
    8: 'Fácil',
    10: 'Muy fácil'
  };

  options = {
    0: 'Nunca',
    1: 'Casi nunca',
    2: 'De vez en cuand',
    3: 'A menudo',
    4: 'Muy a menudo',
  };

  genderEnum = GenderEnum;

  params: Params;
  queries: Params;
  recordTypeEnum = RecordTypeEnum;

  patient: IPatient;
  basicMeasurements: any[];
  otherMeasurements: any[];

  patientGoalNumberType = PatientGoalNumberTypeEnum;

  userLogged: IUser;

  currentDate = new Date();
  loaded: boolean;

  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private utilitiesService: UtilitiesService,
    private storageService: StorageService,
  ) {}

  ngOnInit(): void {
    this.loaded = false;
    this.listeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next('');
    this.destroy$.complete();
  }

  listeners(): void {
    this.recordMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async recordMode => {
        try {
          const isSphereOpen = await firstValueFrom(this.isSphereOpen$.pipe(take(1)));
          if (recordMode && isSphereOpen) {
            this.sphereType = recordMode;
            this.loaded = false;
            await this.getData();
            setTimeout(async () => {
              this.loadData = true;
            }, 500);
          }
        } catch (ex) {
          console.error(ex);
        }
      });
  }

  async onShow(): Promise<void> {
    await firstValueFrom(this.store.dispatch(new PatientSphereAction.OpenSphere(true)));
    this.loadData = false;
    this.getRelevantData().then();
  }

  async getRelevantData(): Promise<void> {
    this.userLogged = this.storageService.getLocalItem<{token: string, user: IUser}>('user')?.user;
    this.patient = cloneDeep(await firstValueFrom(this.store.selectOnce(PatientState.getSelectedPatient)));
    this.queries = this.utilitiesService.getAllQueryParams(this.activatedRoute);
    let expertId: string;
    if (this.userLogged && this.userLogged.experts) {
      expertId = this.userLogged.experts[0]._id;
    } else if (this.userLogged && this.userLogged.patients) {
      const queryParams: Params = this.activatedRoute.snapshot.queryParams;
      expertId = queryParams.expert;
    }

    this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
    if (!this.serviceId) {
      await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patient._id, expertId)));
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
    }

    const service: IService = await this.store.selectSnapshot(ServiceState.getActive);
    const [servicePatient] = service.servicePatients ?? [];

    this.sphereType = servicePatient?.recordMode ?? SphereModeEnum.MINI;

    await this.getData();

    setTimeout(() => {
      this.scrollComponent?.scrollTo({top: 0, duration: 0});
      this.loadData = true;
    }, 500);
  }

  async getData(): Promise<void> {
    if (this.sphereType === SphereModeEnum.MINI) {
      // se obtiene la mini esfera
      await firstValueFrom(this.store.dispatch(new RecordMiniSphereAction.GetActive(this.serviceId, this.patient._id)));
      this.patientRelevantDataMiniSphere = cloneDeep(await firstValueFrom(this.store.selectOnce(MiniSphereState.getRecordMiniSphere)));
    } else if (this.sphereType === SphereModeEnum.COMPLETE) {
      // se obtiene la esfera completa
      await firstValueFrom(this.store.dispatch(new PatientSphereAction.GetPatientSphere()));
      this.patientRelevantData = cloneDeep(await firstValueFrom(this.store.selectOnce(PatientSphereState.getPatientRelevantData)));
      this.basicMeasurements = this.patientRelevantData.measurements.filter((measurement: SPHERE.ISpherePatientMeasurement) =>
        measurement.measurementName === 'imc' || measurement.measurementName === 'height' || measurement.measurementName === 'weight');
      this.otherMeasurements = this.patientRelevantData.measurements
        .filter((measurement: SPHERE.ISpherePatientMeasurement) => !['weight', 'height', 'imc'].includes(measurement.measurementName));
    }
  }

  async onRedirectTo(record: RecordTypeEnum): Promise<void> {
    if (record === RecordTypeEnum.MINI_SPHERE) {
      await this.router.navigate([
        'clinic',
        this.patient.clinicId,
        'patients',
        this.patient._id,
        'history-data'
      ], {
        queryParams: {...this.queries}
      });
    } else if (record === RecordTypeEnum.DIAGNOSTIC) {
      await this.router.navigate([
        'clinic',
        this.patient.clinicId,
        'patients',
        this.patient._id,
        'diagnostic'
      ], {
        queryParams: {...this.queries}
      });
    } else if (record === RecordTypeEnum.MEAL_PLAN) {
      await this.router.navigate([
        'clinic',
        this.patient.clinicId,
        'patients',
        this.patient._id,
        'meal-plans'
      ], {
        queryParams: {...this.queries}
      });
    } else {
      await this.router.navigate([
        'clinic',
        this.patient.clinicId,
        'patients',
        this.patient._id,
        'history-data'
      ], {
        queryParams: {...this.queries, 'active-record': record}
      });
    }
    await firstValueFrom(this.store.dispatch(new PatientSphereAction.OpenSphere(false)));
  }

}
